import React, { useState } from "react";
import { Tabs, Badge, Collapse } from "antd";
import type { TabsProps } from "antd";
import { ChevronDown } from "lucide-react";
import { UrgentTasks } from "./UrgentTasks";
import { RecentActivity } from "./RecentActivity";
import { Alerts } from "./Alerts";
import { useGetMostUrgentTasks } from "~/services/task";
import { useGetAlerts } from "~/services/alert/alert.queries";

export const ActivityTabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState("urgent-tasks");

  // Fetch data for counts
  const { data: tasks } = useGetMostUrgentTasks();
  const { data: alertsData } = useGetAlerts({ page: 1, pageSize: 100, status: "open" });

  // Calculate urgent tasks count (open/pending tasks)
  const urgentTasksCount = tasks?.filter(task => task.taskStatus !== "COMPLETED").length || 0;

  // Calculate alerts count (upcoming and overdue)
  const alertsCount = alertsData?.items?.length || 0;

  const items: TabsProps["items"] = [
    {
      key: "urgent-tasks",
      label: (
        <div className="flex items-center space-x-2">
          <span>Urgent Tasks</span>
          {urgentTasksCount > 0 && (
            <Badge count={urgentTasksCount} showZero={false} />
          )}
        </div>
      ),
      children: <UrgentTasks />,
    },
    {
      key: "recent-activity",
      label: "Recent Activity",
      children: <RecentActivity />,
    },
    {
      key: "alerts",
      label: (
        <div className="flex items-center space-x-2">
          <span>Alerts</span>
          {alertsCount > 0 && (
            <Badge count={alertsCount} showZero={false} />
          )}
        </div>
      ),
      children: <Alerts />,
    },
  ];

  const collapseItems = [
    {
      key: "1",
      label: (
        <div className="flex items-center justify-between w-full pr-4">
          <span className="font-medium text-base">Activity Overview</span>
          <div className="flex items-center gap-6">
            {urgentTasksCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Urgent Tasks:</span>
                <Badge count={urgentTasksCount} showZero={false} />
              </div>
            )}
            {alertsCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Alerts:</span>
                <Badge count={alertsCount} showZero={false} />
              </div>
            )}
          </div>
        </div>
      ),
      children: (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={items}
          className="-mt-4"
        />
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg border">
      <Collapse
        items={collapseItems}
        defaultActiveKey={[]}
        expandIcon={({ isActive }) => (
          <ChevronDown
            size={20}
            className={`transition-transform ${isActive ? "rotate-180" : ""}`}
          />
        )}
        className="bg-transparent border-none"
      />
    </div>
  );
};
